import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import { useActiveSpace } from "./useActiveSpace";

// ── Types ────────────────────────────────────────────────────────────────────

export interface OwnerProfile {
  profileId: string;
  nickname: string;
  avatarUrl: string | null;
}

/**
 * A space member as returned by useTribeMembers.
 *
 * `connectionId` is the space_members.id — used as the opaque reference
 * key in ParticipantRef and ParticipantPicker (replaces child_connections.id).
 */
export interface TribeMember {
  connectionId: string;        // space_members.id
  profileId: string | null;    // space_members.user_id (null = unregistered)
  role: "member" | "contributor";
  name: string;
}

export interface TribeMembersResult {
  owner: OwnerProfile | null;
  coparents: TribeMember[];    // role='member' space members
  contributors: TribeMember[]; // role='contributor' space members
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

interface TribeMembersData {
  owner: OwnerProfile | null;
  coparents: TribeMember[];
  contributors: TribeMember[];
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the owner profile + accepted space members split into
 * "co-parents" (role='member') and contributors (role='contributor').
 *
 * Queries space_members (replaces the old child_connections query).
 * RLS ensures only members of the caller's own space are returned.
 */
export function useTribeMembers(): TribeMembersResult {
  const { user } = useAuth();
  const { space: activeSpace, isLoading: spaceLoading } = useActiveSpace();
  const spaceId = activeSpace?.id ?? null;

  const query = useQuery({
    queryKey: ["tribe-members", spaceId],
    enabled: !!user && !spaceLoading && !!spaceId,
    queryFn: async (): Promise<TribeMembersData> => {
      // Fetch the caller's profile and all active space members IN THE ACTIVE
      // SPACE in parallel. Without the space_id filter, RLS returns members
      // across every space the caller belongs to, which would pollute the
      // participant picker with unrelated people from other families.
      const [{ data: profile }, { data: members }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, nickname, avatar_url")
          .eq("id", user!.id)
          .maybeSingle(),

        supabase
          .from("space_members")
          .select("id, user_id, role, display_name, status")
          .eq("space_id", spaceId!)
          .eq("status", "active")
          // Exclude the owner row so they don't appear as a tribe chip in
          // the picker — they're represented by the ME chip instead.
          // Also exclude role='child' rows: Kindle-kid accounts are tagged
          // via their `children` row (with hasActiveChildAccount resolved
          // in useChildren), not as tribe chips.
          .neq("role", "owner")
          .neq("role", "child"),
      ]);

      let owner: OwnerProfile | null = null;
      if (profile) {
        const p = profile as { id: string; nickname: string; avatar_url: string | null };
        owner = { profileId: p.id, nickname: p.nickname, avatarUrl: p.avatar_url };
      }

      const copa: TribeMember[]    = [];
      const contrib: TribeMember[] = [];

      for (const row of ((members ?? []) as Array<{
        id: string;
        user_id: string | null;
        role: string;
        display_name: string | null;
        status: string;
      }>).filter((m) => m.user_id !== user!.id)) {
        const member: TribeMember = {
          connectionId: row.id,
          profileId: row.user_id,
          role: row.role === "member" ? "member" : "contributor",
          name: row.display_name?.trim() || (row.role === "member" ? "Co-parent" : "Contributor"),
        };

        if (row.role === "member") copa.push(member);
        else contrib.push(member);
      }

      return { owner, coparents: copa, contributors: contrib };
    },
  });

  return {
    owner: query.data?.owner ?? null,
    coparents: query.data?.coparents ?? [],
    contributors: query.data?.contributors ?? [],
    isLoading: query.isLoading || spaceLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
